import { Loader2, Volume2 } from "lucide-react";
import { speak } from "@/lib/voice";

export function Loader({ label = "Thinking…" }) {
  return (
    <div className="flex items-center gap-3 text-muted-foreground py-8 animate-pulse" data-testid="loader">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="font-head">{label}</span>
    </div>
  );
}

export function SpeakButton({ text, className = "" }) {
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={() => speak(typeof text === "string" ? text : JSON.stringify(text))}
      data-testid="speak-button"
      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground transition-colors ${className}`}
    >
      <Volume2 className="w-3.5 h-3.5" /> Read aloud
    </button>
  );
}

export function Chips({ items = [], onClick, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={onClick ? () => onClick(it) : undefined}
          className={`rounded-full border border-border px-3 py-1.5 text-sm bg-card hover:border-primary hover:text-primary transition-colors ${onClick ? "cursor-pointer" : "cursor-default"}`}
        >
          {typeof it === "string" ? it : it.name || it.area || JSON.stringify(it)}
        </button>
      ))}
    </div>
  );
}

export function Bullets({ items = [], testid }) {
  if (!items || !items.length) return null;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-[0.95rem] leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span>{typeof it === "string" ? it : (it.msg || JSON.stringify(it))}</span>
        </li>
      ))}
    </ul>
  );
}

// Render simple markdown-ish text (headings, bullets, paragraphs)
export function RichText({ text }) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return (
    <div className="space-y-2 leading-relaxed text-[0.95rem]">
      {lines.map((raw, i) => {
        const line = raw.trim();
        if (!line) return null;
        if (line.startsWith("### ")) return <h4 key={i} className="font-head font-bold text-base mt-3">{line.slice(4)}</h4>;
        if (line.startsWith("## ")) return <h3 key={i} className="font-head font-bold text-lg mt-3">{line.slice(3)}</h3>;
        if (line.startsWith("# ")) return <h2 key={i} className="font-head font-bold text-xl mt-3">{line.slice(2)}</h2>;
        if (/^[-*]\s/.test(line)) return <div key={i} className="flex gap-2 pl-1"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span>{clean(line.slice(2))}</span></div>;
        if (/^\d+\.\s/.test(line)) return <div key={i} className="flex gap-2 pl-1"><span className="font-bold text-primary">{line.match(/^\d+/)[0]}.</span><span>{clean(line.replace(/^\d+\.\s/, ""))}</span></div>;
        return <p key={i}>{clean(line)}</p>;
      })}
    </div>
  );
}

function clean(s) { return s.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1"); }

export function SectionCard({ title, icon: Icon, children, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-5 sm:p-6 animate-rise ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
    >
      {title && (
        <div className="flex items-center gap-2 mb-3">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          <h3 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}
